import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { AtnaAssistant } from '../components/atna/AtnaAssistant';
import { ATNA_CONFIG } from '../components/atna/atnaConfig';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function Demo() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Demo'
    });
  }, []);
  const modules = [{
    to: '/requests',
    fr: 'Requêtes ATNA (311)',
    en: 'ATNA Requests (311)'
  }, {
    to: '/collect',
    fr: 'Collectes ATNA',
    en: 'ATNA Waste Collection'
  }, {
    to: '/communications',
    fr: 'Communications ATNA',
    en: 'ATNA Communications'
  }, {
    to: '/smart-water',
    fr: 'Compteurs d’eau ATNA',
    en: 'ATNA Smart Water'
  }, {
    to: '/digital-card',
    fr: 'Carte numérique BCITI',
    en: 'BCITI Digital Card'
  }, {
    to: '/analytics',
    fr: 'Analytique',
    en: 'Analytics'
  }];
  return <div className="w-full min-h-screen bg-white" data-id="element-8303" data-mp="1pp4k21lg1e3g">
      <Header data-id="element-8304" data-mp="1pnjruimlq78r" />
      <main data-id="element-8305" data-mp="1pmrzdgoawpmp">
        {/* Hero */}
        <section className="bg-white pt-40 lg:pt-48 pb-16 px-6" data-id="element-8306" data-mp="1kp8jydfva6t6">
          <div className="max-w-4xl mx-auto text-center" data-id="element-8307" data-mp="1knf9en24v2fw">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-8308" data-mp="1frzxw1rllimn">
              {isFR ? 'Démonstration en direct' : 'See it live'}
            </motion.div>
            <motion.h1 {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-[#121212] leading-[1.1] tracking-tight mb-6" data-id="element-8309" data-mp="w555kgfcbcwl">
              {isFR ? 'Voir BCITI en action' : 'See BCITI in action'}
            </motion.h1>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-lg md:text-xl text-[#121212]/60 leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-8310" data-mp="s54osp4rqpmd">
              {isFR ? 'Essayez l’assistant ATNA, parcourez les résultats en production et explorez chaque module — en un seul endroit.' : 'Try the ATNA assistant, review production results and explore each module — all in one place.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} data-id="element-8311" data-mp="23k06yl2mj97o">
              <Link to="/request-demo" className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-sm font-medium uppercase tracking-wider hover:bg-[#4F46E5]/90 transition-colors" data-id="element-8312" data-mp="28um2c8soc7bc">
                {isFR ? 'Demander une démo' : 'Request a demo'}
                <ArrowRightIcon className="w-4 h-4" data-id="element-8313" data-mp="1tz65jw0plxb9" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Live ATNA assistant */}
        <section className="py-20 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8314" data-mp="23x3r2br6pg06">
          <div className="max-w-3xl mx-auto" data-id="element-8315" data-mp="1yxhfe6r087th">
            <div className="text-center mb-10" data-id="element-8316" data-mp="1ywptjnn7loge">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-4" data-id="element-8317" data-mp="3fbr2kxjifqiq">
                {isFR ? 'Assistant ATNA' : 'ATNA assistant'}
              </div>
              <h2 className="font-serif text-3xl md:text-4xl text-[#121212]" data-id="element-8318" data-mp="3af0ky5s24mv7">
                {isFR ? 'Posez une question. En direct.' : 'Ask a question. Live.'}
              </h2>
            </div>
            <AtnaAssistant vertical={ATNA_CONFIG['home']} page="/demo" data-id="element-8319" data-mp="1owgoogxmmwb5" />
          </div>
        </section>

        {/* Production results */}
        <section className="py-24 px-6 border-t border-[#E4E6EC]" data-id="element-8320" data-mp="1xvk7rkwsnbpn">
          <div className="max-w-4xl mx-auto" data-id="element-8321" data-mp="22uepejj0yu86">
            <div className="text-center font-sans text-xs font-semibold tracking-[0.2em] uppercase text-[#4F46E5] mb-10" data-id="element-8322" data-mp="22tltkdlltp4t">
              {isFR ? 'Résultats en production' : 'Production results'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 border border-[#E4E6EC] divide-y md:divide-y-0 md:divide-x divide-[#E4E6EC]" data-id="element-8323" data-mp="2ckq676nt4xwh">
              <div className="p-8 md:p-12 text-center" data-id="element-8324" data-mp="2lkwiwqboltul">
                <div className="font-serif text-5xl md:text-6xl text-[#121212] leading-none mb-5" data-id="element-8325" data-mp="2lmhd2eu33arx">
                  400 <span className="text-[#4F46E5]" data-id="element-8326" data-mp="2qk91uzzfmgrw">→</span> 5
                </div>
                <div className="font-sans text-sm text-[#121212]/60 leading-relaxed max-w-xs mx-auto" data-id="element-8327" data-mp="exfbxjjeg79h">
                  {isFR ? 'Personnes en attente au comptoir pour renouveler leur carte citoyenne' : 'People waiting at the counter to renew their citizen card'}
                </div>
              </div>
              <div className="p-8 md:p-12 text-center" data-id="element-8328" data-mp="z7fdcq9z7yu">
                <div className="font-serif text-5xl md:text-6xl text-[#121212] leading-none mb-5" data-id="element-8329" data-mp="xlu3c7xmpnat">
                  {isFR ? '130 000' : '130,000'}
                  <span className="text-[#4F46E5]" data-id="element-8330" data-mp="outq1chuojym">+</span>
                </div>
                <div className="font-sans text-sm text-[#121212]/60 leading-relaxed max-w-xs mx-auto" data-id="element-8331" data-mp="ospgadx9sqqf">
                  {isFR ? 'Citoyens joignables en moins de 30 minutes lors d’une urgence' : 'Residents reachable in under 30 minutes during an emergency'}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Explore modules */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8332" data-mp="20fgb8ng5bqec">
          <div className="max-w-5xl mx-auto" data-id="element-8333" data-mp="20hkdl0yechci">
            <div className="text-center mb-12" data-id="element-8334" data-mp="1mn9v31cztjew">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-4" data-id="element-8335" data-mp="1mmiasmouj83x">
                {isFR ? 'Modules' : 'Modules'}
              </div>
              <h2 className="font-serif text-3xl md:text-4xl text-[#121212]" data-id="element-8336" data-mp="1cokerd0qw70r">
                {isFR ? 'Explorez chaque module' : 'Explore each module'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-8337" data-mp="13qs44m5yxnjp">
              {modules.map(m => <Link key={m.to} to={m.to} className="border border-[#E4E6EC] bg-white p-6 flex items-center justify-between hover:border-[#4F46E5]/30 transition-colors" data-id="element-8338" data-mp="17rkexjuho0i3">
                  <span className="font-serif text-lg text-[#121212]" data-id="element-8339" data-mp="qhgkgpkh3896">
                    {isFR ? m.fr : m.en}
                  </span>
                  <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-8340" data-mp="mh66apmj1bj8" />
                </Link>)}
            </div>
          </div>
        </section>

        {/* Case study */}
        <section className="py-24 px-6 border-t border-[#E4E6EC]" data-id="element-8341" data-mp="vgkqj7c3opva">
          <div className="max-w-3xl mx-auto text-center" data-id="element-8342" data-mp="1a5p9wrumt04z">
            <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-4" data-id="element-8343" data-mp="1a45vz7wg716d">
              {isFR ? 'Étude de cas' : 'Case study'}
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#121212] mb-6" data-id="element-8344" data-mp="1j5cflpue8s3u">
              {isFR ? 'Brossard, en production depuis 2017' : 'Brossard, live since 2017'}
            </h2>
            <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-base font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-8345" data-mp="3kwyyt581nipr">
              {isFR ? 'Explorer l’étude de cas' : 'Explore the case study'}
              <ArrowRightIcon className="w-4 h-4" data-id="element-8346" data-mp="2z430w8bbzfr9" />
            </Link>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8347" data-mp="33wme3463w8vj">
          <div className="max-w-3xl mx-auto text-center" data-id="element-8348" data-mp="38unvlknvw04w">
            <h2 className="font-serif text-3xl md:text-4xl text-[#121212] mb-8" data-id="element-8349" data-mp="38w8s3ftrdt5e">
              {isFR ? 'Prêt à voir BCITI à l’œuvre avec vos données ?' : 'Ready to see BCITI at work with your data?'}
            </h2>
            <Link to="/request-demo" className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-sm font-medium uppercase tracking-wider hover:bg-[#4F46E5]/90 transition-colors" data-id="element-8350" data-mp="2l3s82zqgnr3g">
              {isFR ? 'Demander une démo' : 'Request a demo'}
              <ArrowRightIcon className="w-4 h-4" data-id="element-8351" data-mp="5cmkn2n4e9ai" />
            </Link>
          </div>
        </section>
      </main>
      <Footer data-id="element-8352" data-mp="1pv6tehpr04hd" />
    </div>;
}